import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { actionCardsApi, draftsApi, teamsApi, foldersApi, templatesApi } from '@/utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FilterSelect } from '@/components/FilterSelect';
import { toast } from 'sonner';
import {
  Upload, ArrowRight, Calendar, User, Tag, X,
  FileAudio, Zap, Mic, Square, Pause, Play, Save, Loader2, Trash2, Users, Folder, LayoutTemplate, Paperclip, Plus
} from 'lucide-react';

const TODAY = new Date().toLocaleDateString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric'
});

const PROCESSING_STEPS_TEXT = [
  { pct: 20, label: 'Analyzing conversation\u2026' },
  { pct: 50, label: 'Extracting action items\u2026' },
  { pct: 80, label: 'Structuring data\u2026' },
  { pct: 95, label: 'Almost done\u2026' },
];

const PROCESSING_STEPS_AUDIO = [
  { pct: 15, label: 'Uploading audio\u2026' },
  { pct: 40, label: 'Transcribing recording\u2026' },
  { pct: 70, label: 'Analyzing conversation\u2026' },
  { pct: 88, label: 'Extracting action items\u2026' },
  { pct: 95, label: 'Structuring data\u2026' },
];

function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function clipTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
}

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `tmp-${Date.now()}-${Math.random()}`);

export default function NewActionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const notesRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const segTimeRef = useRef(0);
  const draftIdRef = useRef(null);
  const pendingUploadsRef = useRef(0);
  const leaveRef = useRef();
  const [resumedClips, setResumedClips] = useState(0);
  const uploadClipRef = useRef(null);

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [tags, setTags] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [contextFile, setContextFile] = useState(null);
  const ctxFileRef = useRef(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [editingChip, setEditingChip] = useState(null); // 'email' | 'tag'
  const [dragging, setDragging] = useState(false);

  const [recState, setRecState] = useState('idle'); // idle | recording | paused
  const [segTime, setSegTime] = useState(0);
  const [clips, setClips] = useState([]); // {id, duration, filename, busy?}
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  const setDraft = (id) => { draftIdRef.current = id; setDraftId(id); };

  const animateProgress = (steps) => {
    let i = 0;
    const tick = () => {
      if (i < steps.length) {
        setProgress(steps[i].pct);
        setProgressLabel(steps[i].label);
        i++;
        if (steps[i - 1]?.pct < 95) setTimeout(tick, 1400);
      }
    };
    tick();
  };

  // ─── Persist a clip to the backend draft (auto-save) ───
  const uploadClip = async (file, duration) => {
    const tempId = newId();
    pendingUploadsRef.current += 1;
    setClips(prev => [...prev, { id: tempId, duration, filename: file.name, busy: true }]);
    try {
      if (!draftIdRef.current) {
        const fd = new FormData();
        fd.append('title', title || '');
        fd.append('contact_email', contactEmail || '');
        fd.append('tags', tags.join(','));
        fd.append('notes', notes || '');
        if (duration != null) fd.append('duration', duration);
        fd.append('file', file);
        const res = await draftsApi.create(fd);
        setDraft(res.data.id);
        const created = res.data.clips?.[res.data.clips.length - 1] || {};
        setClips(prev => prev.map(c => c.id === tempId ? { ...created, busy: false } : c));
      } else {
        const fd = new FormData();
        fd.append('file', file);
        if (duration != null) fd.append('duration', duration);
        const res = await draftsApi.addClip(draftIdRef.current, fd);
        setClips(prev => prev.map(c => c.id === tempId ? { ...res.data, busy: false } : c));
      }
    } catch (err) {
      toast.error('Failed to save audio clip');
      setClips(prev => prev.filter(c => c.id !== tempId));
    } finally {
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
    }
  };
  uploadClipRef.current = uploadClip;

  // ─── Recording: pause = new clip, resume = next clip ───
  const startSegment = async () => {
    try {
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const mr = new MediaRecorder(streamRef.current, { mimeType });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) return;
        const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
        const file = new File([blob], `segment-${Date.now()}.${ext}`, { type: mimeType });
        uploadClipRef.current(file, segTimeRef.current);
      };
      mr.start(250);
      segTimeRef.current = 0;
      setSegTime(0);
      setRecState('recording');
      timerRef.current = setInterval(() => { segTimeRef.current += 1; setSegTime(t => t + 1); }, 1000);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone permission.');
    }
  };

  const stopRecorder = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
  };

  const pauseRecording = () => { stopRecorder(); setRecState('paused'); };

  const finishRecording = () => {
    stopRecorder();
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setRecState('idle');
  };

  const handleFile = (file) => {
    if (!file) return;
    uploadClipRef.current(file, null);
    toast.success('Audio added');
  };

  const removeClip = async (clip) => {
    if (clip.busy) return;
    setClips(prev => prev.filter(c => c.id !== clip.id));
    if (draftIdRef.current && clip.id) {
      try { await draftsApi.deleteClip(draftIdRef.current, clip.id); } catch (e) { /* noop */ }
    }
  };

  const saveDraft = async () => {
    if (!draftIdRef.current && clips.length === 0 && !notes.trim()) {
      toast.error('Record audio or add notes before saving a draft');
      return;
    }
    if (recState !== 'idle') finishRecording();
    setSavingDraft(true);
    try {
      if (!draftIdRef.current) {
        const fd = new FormData();
        fd.append('title', title || '');
        fd.append('contact_email', contactEmail || '');
        fd.append('tags', tags.join(','));
        fd.append('notes', notes || '');
        const res = await draftsApi.create(fd);
        setDraft(res.data.id);
      } else {
        await draftsApi.update(draftIdRef.current, { title, contact_email: contactEmail, notes, tags });
      }
      toast.success('Draft saved');
      navigate('/drafts');
    } catch (err) {
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Flush the in-progress recording, wait for the audio to finish uploading to the draft,
  // then resolve with the draft id. Used when the user chooses to leave mid-recording.
  const finalizeDraftAndLeave = async () => {
    if (recState !== 'idle') finishRecording();
    await new Promise(r => setTimeout(r, 350)); // let mr.onstop enqueue the final clip
    const start = Date.now();
    while (pendingUploadsRef.current > 0 && Date.now() - start < 12000) {
      await new Promise(r => setTimeout(r, 200));
    }
    if (draftIdRef.current) {
      try { await draftsApi.update(draftIdRef.current, { title, contact_email: contactEmail, notes, tags }); } catch { /* noop */ }
    }
    return draftIdRef.current;
  };
  leaveRef.current = finalizeDraftAndLeave;

  const handleGenerate = async () => {
    if (recState !== 'idle') finishRecording();
    if (clips.some(c => c.busy)) {
      toast.error('Audio is still uploading — try again in a moment');
      return;
    }
    if (!notes.trim() && clips.length === 0 && !draftIdRef.current) {
      toast.error('Add notes or record/upload audio first');
      return;
    }
    setLoading(true);
    try {
      let res;
      if (draftIdRef.current) {
        animateProgress(clips.length ? PROCESSING_STEPS_AUDIO : PROCESSING_STEPS_TEXT);
        await draftsApi.update(draftIdRef.current, { title, contact_email: contactEmail, notes, tags });
        const pfd = new FormData();
        if (templateId) pfd.append('template_id', templateId);
        if (contextFile) pfd.append('context_file', contextFile);
        res = await draftsApi.process(draftIdRef.current, teamId || undefined, pfd);
      } else {
        animateProgress(PROCESSING_STEPS_TEXT);
        const fd = new FormData();
        fd.append('notes', notes);
        fd.append('contact_name', title || '');
        fd.append('contact_email', contactEmail || '');
        fd.append('tags', tags.join(','));
        if (teamId) fd.append('team_id', teamId);
        if (templateId) fd.append('template_id', templateId);
        if (contextFile) fd.append('context_file', contextFile);
        res = await actionCardsApi.captureText(fd);
      }
      setProgress(100);
      setProgressLabel('Done!');
      if (folderId && res?.data?.id) {
        try { await actionCardsApi.move(res.data.id, folderId, teamId || null); } catch { /* non-fatal */ }
      }
      toast.success('Action Card created!');
      setTimeout(() => navigate(`/action-cards/${res.data.id}`), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process — please try again');
      setLoading(false);
    }
  };

  const addTag = (val) => {
    const clean = val.trim();
    if (clean && !tags.includes(clean)) setTags(prev => [...prev, clean]);
    setTagInput('');
    setEditingChip(null);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('audio')) handleFile(file);
  };

  const loadDraft = async (id, autoRecord = false) => {
    try {
      const res = await draftsApi.get(id);
      const d = res.data;
      setDraft(d.id);
      setTitle(d.title || '');
      setContactEmail(d.contact_email || '');
      setNotes(d.notes || '');
      setTags(d.tags || []);
      const existing = (d.clips || []).map(c => ({ ...c, busy: false }));
      setClips(existing);
      setResumedClips(existing.length);
      if (autoRecord) {
        toast.success('Resuming — recording a new segment');
        startSegment();
      } else {
        toast.success('Draft loaded — resume where you left off');
      }
    } catch (err) {
      toast.error('Could not load that draft');
    }
  };

  const wordCount = notes.trim() ? notes.trim().split(/\s+/).length : 0;
  // Folder choices follow the selected workspace (folders are optional organization inside it).
  const folderOptions = folders.filter(f => f.scope === 'team' && f.team_id === teamId);

  const createWorkspaceFolder = async (name) => {
    const clean = name.trim();
    setCreatingFolder(false); setNewFolderName('');
    if (!clean || !teamId) return;
    try {
      const res = await foldersApi.create({ name: clean, scope: 'team', team_id: teamId });
      setFolders(prev => [...prev, res.data]);
      setFolderId(res.data.id);
      toast.success(`Folder “${clean}” created in the workspace`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create folder'); }
  };

  // Honour ?draft=<id> (resume) or ?mode=record|upload|paste from the capture modal.
  useEffect(() => {
    teamsApi.list().then(r => {
      const ts = (r.data.teams || []).filter(t => t.is_member && t.my_role !== 'viewer');
      setTeams(ts);
      const t = searchParams.get('team');
      if (t && ts.some(x => x.id === t)) setTeamId(t);
      else { const d = ts.find(x => x.is_default); if (d) setTeamId(d.id); }
    }).catch(() => {});
    foldersApi.list().then(r => {
      const fs = r.data.folders || [];
      setFolders(fs);
      const f = searchParams.get('folder');
      if (f && fs.some(x => x.id === f)) setFolderId(f);
    }).catch(() => {});
    templatesApi.list().then(r => setTemplates(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const did = searchParams.get('draft');
    if (did) { loadDraft(did, searchParams.get('mode') === 'record'); return; }
    const mode = searchParams.get('mode');
    if (!mode) return;
    const t = setTimeout(() => {
      if (mode === 'upload') fileInputRef.current?.click();
      else if (mode === 'paste') notesRef.current?.focus();
      else if (mode === 'record') startSegment();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Stop tracks if the component unmounts mid-recording.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  // Warn before leaving while a recording is in progress (browser close/refresh + in-app links).
  useEffect(() => {
    if (recState === 'idle') return undefined;
    const WARN = 'You are recording a capture. Leaving this page will stop your current recording. Proceed?';
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = WARN; return WARN; };
    const onClickCapture = (e) => {
      const a = e.target.closest?.('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || a.target === '_blank') return;
      if (!window.confirm(WARN)) { e.preventDefault(); e.stopPropagation(); return; }
      // User chose to leave → block the nav, auto-save the recording as a draft, then continue.
      e.preventDefault(); e.stopPropagation();
      const dest = href.replace(window.location.origin, '') || '/drafts';
      toast.loading('Saving your recording as a draft…', { id: 'rec-save' });
      (leaveRef.current ? leaveRef.current() : Promise.resolve())
        .then(() => { toast.success('Recording saved to Drafts', { id: 'rec-save' }); navigate(dest); })
        .catch(() => { toast.dismiss('rec-save'); navigate(dest); });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [recState, navigate]);

  // ─── Processing screen ───
  if (loading) {
    const isAudio = clips.length > 0;
    return (
      <div className="ce-proc" data-testid="capture-processing">
        <div className="pic"><Zap size={26} fill="currentColor" /></div>
        <h2>Generating Action Card</h2>
        <p className="pl">{progressLabel || (isAudio ? 'Transcribing recording…' : 'Analyzing conversation…')}</p>
        <div className="pbar"><i style={{ width: `${progress}%` }} /></div>
        <div className="ppct">{progress}%</div>
      </div>
    );
  }

  const totalDuration = clips.reduce((sum, c) => sum + (c.duration || 0), 0);

  return (
    <div
      className="cap-edit"
      data-testid="capture-page"
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      {dragging && (
        <div className="ce-drag">
          <div className="box"><FileAudio /><p>Drop audio to capture</p></div>
        </div>
      )}

      <div className="cap-edit-head">
        {draftId ? (
          <span className="ce-draft-flag" data-testid="capture-draft-flag"><Save />Resuming draft</span>
        ) : (
          <span className="lab">Capture</span>
        )}
        <input
          ref={titleRef}
          className="ce-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Conversation title — client name or topic"
          data-testid="capture-title-input"
        />
      </div>

      {resumedClips > 0 && (
        <div className="ce-resume-banner" data-testid="capture-resume-banner">
          <Mic size={14} />
          <span>Resumed from draft · <strong>{resumedClips}</strong> earlier clip{resumedClips === 1 ? '' : 's'} — new audio is added to this recording.</span>
        </div>
      )}

      {/* Meta chips */}
      <div className="ce-chips">
        <div className="ce-chip static"><Calendar />{TODAY}</div>

        {teams.length > 0 && (
          <FilterSelect
            testid="capture-workspace-select"
            icon={Users}
            placeholder="Workspace"
            value={teamId}
            options={teams.map(t => ({ value: t.id, label: t.name }))}
            onChange={v => { setTeamId(v); setFolderId(''); }}
          />
        )}

        {creatingFolder ? (
          <input autoFocus className="ce-chip-input" value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onBlur={() => createWorkspaceFolder(newFolderName)}
            onKeyDown={e => {
              if (e.key === 'Enter') createWorkspaceFolder(newFolderName);
              if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
            }}
            placeholder="New folder name" style={{ width: 160 }} data-testid="capture-new-folder-input" />
        ) : (folderOptions.length > 0 || teamId) && (
          <Select value={folderId || 'none'} onValueChange={v => {
            if (v === '__new__') { setCreatingFolder(true); return; }
            setFolderId(v === 'none' ? '' : v);
          }}>
            <SelectTrigger data-testid="capture-folder-select"
              className={`ce-chip${folderId ? ' active' : ''} h-auto border-0 shadow-none gap-1.5 w-auto`}
              style={{ padding: '0 10px', minHeight: 34 }}>
              <Folder size={14} />
              <SelectValue placeholder="No folder" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="none">No folder</SelectItem>
              {folderOptions.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              {teamId && (
                <SelectItem value="__new__" data-testid="capture-folder-new-option">
                  <span className="flex items-center gap-1.5" style={{ color: '#6994c6' }}><Plus size={12} />New folder…</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}

        {templates.length > 0 && (
          <FilterSelect
            testid="capture-template-select"
            icon={LayoutTemplate}
            placeholder="No template"
            value={templateId}
            options={templates.map(t => ({ value: t.id, label: t.name }))}
            onChange={v => setTemplateId(v)}
          />
        )}

        {contextFile ? (
          <span className="ce-chip active" data-testid="capture-context-chip">
            <Paperclip size={13} />{contextFile.name}
            <span className="x" onClick={() => setContextFile(null)} data-testid="capture-context-remove"><X size={11} /></span>
          </span>
        ) : (
          <button className="ce-chip" onClick={() => ctxFileRef.current?.click()} data-testid="capture-context-attach">
            <Paperclip />Attach agenda
          </button>
        )}

        {editingChip === 'email' ? (
          <input autoFocus className="ce-chip-input" value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            onBlur={() => setEditingChip(null)}
            onKeyDown={e => { if (e.key === 'Enter') setEditingChip(null); }}
            placeholder="contact@email.com" style={{ width: 180 }} />
        ) : (
          <button className={`ce-chip${contactEmail ? ' active' : ''}`} onClick={() => setEditingChip('email')}>
            <User />{contactEmail || 'Add contact email'}
          </button>
        )}

        {tags.map(tag => (
          <span key={tag} className="ce-chip active">{tag}<span className="x" onClick={() => setTags(prev => prev.filter(t => t !== tag))}><X size={11} /></span></span>
        ))}

        {editingChip === 'tag' ? (
          <input autoFocus className="ce-chip-input" value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onBlur={() => addTag(tagInput)}
            onKeyDown={e => { if (e.key === 'Enter') addTag(tagInput); if (e.key === 'Escape') { setTagInput(''); setEditingChip(null); } }}
            placeholder="Tag name" style={{ width: 110 }} />
        ) : (
          <button className="ce-chip" onClick={() => setEditingChip('tag')}><Tag />Add tag</button>
        )}
      </div>

      {/* Recorded / uploaded clips */}
      {clips.length > 0 && (
        <div className="ce-clips" data-testid="capture-clips-list">
          {clips.map((clip, i) => (
            <div key={clip.id} className={`ce-clip${clip.busy ? ' busy' : ''}`} data-testid={`capture-clip-${i}`}>
              <span className="ci">{clip.busy ? <Loader2 className="spin" /> : <FileAudio />}</span>
              <span className="cn">Segment {i + 1}{clip.created_at && !clip.busy ? <span className="ct"> · {clipTime(clip.created_at)}</span> : ''}</span>
              <span className="cd">{clip.busy ? 'Saving…' : (clip.duration ? formatTime(clip.duration) : 'Audio')}</span>
              <button className="cx" onClick={() => removeClip(clip)} disabled={clip.busy} data-testid={`capture-clip-delete-${i}`} aria-label="Delete clip">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Notes */}
      <textarea
        ref={notesRef}
        className="ce-notes"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder={clips.length > 0
          ? 'Audio captured — add any extra context or notes here…'
          : 'Write notes, paste a transcript, or describe what was discussed…\n\nInclude: who you talked to, key points, decisions made, and what needs to happen next.'}
        data-testid="capture-notes-input"
        rows={12}
      />
      <div className="ce-count">
        {clips.length > 0
          ? `${clips.length} segment${clips.length > 1 ? 's' : ''}${totalDuration ? ` · ${formatTime(totalDuration)}` : ''} captured${notes.trim() ? ` · ${wordCount} words` : ''}`
          : wordCount > 0 ? `${wordCount} words` : 'Paste or type above…'}
      </div>

      <input
        ref={ctxFileRef}
        type="file"
        accept=".pdf,.docx,.txt,.md"
        className="hidden"
        onChange={e => {
          const f = e.target.files[0];
          if (f) {
            if (f.size > 25 * 1024 * 1024) toast.error('File exceeds the 25 MB limit');
            else { setContextFile(f); toast.success('Agenda attached — it will guide the AI notes'); }
          }
          e.target.value = '';
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm"
        className="hidden"
        onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
      />

      {/* Bottom action bar */}
      <div className="ce-bar-wrap">
        <div className="ce-bar">
          {recState === 'recording' ? (
            <>
              <button className="ce-btn rec" onClick={pauseRecording} data-testid="capture-pause-recording">
                <Pause size={13} fill="currentColor" /><span>Pause · {formatTime(segTime)}</span>
              </button>
              <button className="ce-btn" onClick={finishRecording} data-testid="capture-stop-recording">
                <Square size={12} fill="currentColor" /><span>Done</span>
              </button>
            </>
          ) : recState === 'paused' ? (
            <>
              <button className="ce-btn rec" onClick={startSegment} data-testid="capture-resume-recording">
                <Play size={13} fill="currentColor" /><span>Resume</span>
              </button>
              <button className="ce-btn" onClick={finishRecording} data-testid="capture-stop-recording">
                <Square size={12} fill="currentColor" /><span>Done</span>
              </button>
            </>
          ) : (
            <>
              <button className="ce-btn" onClick={startSegment} title="Start recording" data-testid="capture-record-button">
                <Mic /><span>{clips.length > 0 ? 'Add clip' : 'Record'}</span>
              </button>
              <button className="ce-btn" onClick={() => fileInputRef.current?.click()} data-testid="capture-upload-button">
                <Upload /><span>Upload</span>
              </button>
            </>
          )}

          <div className="ce-status">
            {recState === 'recording'
              ? <span style={{ color: '#B4332E' }}>Recording… Pause to save a clip</span>
              : recState === 'paused'
              ? <span style={{ color: 'var(--blue-d)' }}>Paused — Resume or generate</span>
              : clips.length > 0
              ? <span style={{ color: 'var(--blue-d)' }}>{clips.length} clip{clips.length > 1 ? 's' : ''} ready — generate or save draft</span>
              : notes.length > 0
              ? <span>{wordCount} words — ready to generate</span>
              : <span>Record, upload or paste notes →</span>}
          </div>

          <button className="ce-btn" onClick={saveDraft} disabled={savingDraft} title="Save as draft" data-testid="capture-save-draft-button">
            {savingDraft ? <Loader2 className="spin" /> : <Save />}<span>Save draft</span>
          </button>

          <button className="ce-gen" onClick={handleGenerate} disabled={loading} data-testid="capture-generate-action-card-button">
            Generate <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
